import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowRight, Check, Home, Plus, ShieldCheck, Smartphone, Tv } from 'lucide-react';
import { t } from '@/utils/i18n';
import { useHome } from './useHome';
import { PLATFORMS } from './platforms';
import { Footer, Header } from './Chrome';
import { getHomeCopy, type HomeCopy } from './copy';
import { HeroJourney } from './HeroJourney';
import { QR } from './qr';
import { IPhonePreview } from './IPhonePreview';

const LOGOS = '/assets/brands/real/'; // one-colour official marks, see SOURCES.md
const LOGO_ALT: Record<string, string> = { samsung: 'Samsung', lg: 'LG', roku: 'Roku', firetv: 'Amazon Fire TV', android: 'Android TV', appletv: 'Apple TV' };
const TEXT_MARKS = new Set(['appletv']);
const EASE = [0.22, 1, 0.36, 1] as const;
type Tr = (key: string) => string;

function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const reduced = useReducedMotion();
  return <motion.div className={className} initial={reduced ? false : { opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.8, ease: EASE }}>{children}</motion.div>;
}

/** Original TV capture from tv/store/screenshots/04-home.png. */
function Screen() {
  return <div className="moova-screen moova-tv-capture" aria-hidden="true"><img src="/assets/home/tv-home.webp" alt="" width="1920" height="1080" decoding="async" {...{ fetchpriority: 'high' }} /><div className="moova-tv-chin">moova <i /></div></div>;
}

function Hero({ c, tr }: { c: HomeCopy; tr: Tr }) {
  // Availability per store, built from platforms.ts so it flips with the statuses (store reviewers read this).
  const groups = (['available', 'beta', 'soon'] as const).map(status => ({ label: tr(status === 'available' ? 'devAvailable' : status === 'beta' ? 'devBeta' : 'devSoon'), names: PLATFORMS.filter(p => p.status === status).map(p => p.name.replace(' · ', ' / ')), items: PLATFORMS.filter(p => p.status === status) })).filter(group => group.names.length);
  const availability = groups.map(group => `${group.label}: ${group.names.join(', ')}`).join(' · ');
  return <section id="overview" className="moova-hero" aria-labelledby="moova-title">
    <HeroJourney c={c} tr={tr}><Screen /></HeroJourney>
    <p className="moova-fine moova-demo-note">{c.preview}</p>
    <div className="moova-platform-strip moova-container">
      {/* Full-bleed marquee: four identical copies, the track slides one copy per loop. Reduced motion shows one static row. */}
      <div className="moova-platform-marquee" role="list" aria-label={availability}><div className="moova-platform-track">{[0, 1, 2, 3].map(copy => PLATFORMS.map(p => {
        // Apple: no Apple logo for third parties (Apple marketing guidelines, LEDGER C50), the name is set as text.
        const logo = TEXT_MARKS.has(p.id)
          ? <span key={`${copy}-${p.id}`} role={copy || p.url ? undefined : 'listitem'} className={`moova-logo moova-logo-text moova-logo-${p.id}`} aria-hidden={copy ? true : undefined}>{LOGO_ALT[p.id]}</span>
          : <img key={`${copy}-${p.id}`} role={copy || p.url ? undefined : 'listitem'} className={`moova-logo moova-logo-${p.id}`} src={`${LOGOS}${p.id}.svg`} alt={copy ? '' : LOGO_ALT[p.id]} aria-hidden={copy ? true : undefined} loading="lazy" />;
        // Only the first copy is reachable by keyboard / screen reader; the others are decoration for the loop.
        return p.url ? <a key={`${copy}-${p.id}`} href={p.url} target="_blank" rel="noopener noreferrer" className="moova-logo-link" role={copy ? undefined : 'listitem'} tabIndex={copy ? -1 : undefined} aria-hidden={copy ? true : undefined}>{logo}</a> : logo;
      }))}</div></div>
      <p className="moova-fine moova-platform-status">{groups.map((group, i) => <span key={group.label}>{i > 0 && <i aria-hidden="true" />}<b>{group.label}:</b> {group.items.map((p, j) => <span key={p.id}>{j > 0 && ', '}{p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{group.names[j]}</a> : group.names[j]}</span>)}</span>)}</p>
    </div>
  </section>;
}

/** The eight real capabilities, each illustrated with a capture of the actual TV app (tv/store/screenshots). */
const FEATURE_SHOTS: Record<number, { src: string; pos: string; bleed?: 'left' }> = {
  1: { src: '/assets/home/tv-home.webp', pos: '0 0' },
  2: { src: '/assets/home/tv/live.webp', pos: '100% 0', bleed: 'left' },
  3: { src: '/assets/home/tv/parental.webp', pos: '0 0' },
  4: { src: '/assets/home/tv/playlists.webp', pos: '15% 30%' },
  5: { src: '/assets/home/tv/search.webp', pos: '0 0' },
  7: { src: '/assets/home/tv/language.webp', pos: '0 0' },
  8: { src: '/assets/home/tv/player.webp', pos: '50% 50%' },
};

function Features({ c, tr }: { c: HomeCopy; tr: Tr }) {
  return <section className="moova-experience moova-section" id="experience">
    <div className="moova-container">
      <Reveal className="moova-section-heading"><span className="moova-eyebrow">{tr('ftKicker')}</span><h2>{tr('ftTitleA')}<br /><span>{tr('ftTitleB')}</span></h2><p>{c.cinemaLead}</p></Reveal>
      <div className="moova-feature-list">
        {[1, 2, 3, 4, 5, 6, 7, 8].map(n => {
          const shot = FEATURE_SHOTS[n];
          return <Reveal key={n} className={`moova-feature${n <= 2 ? ' is-wide' : ''}${shot ? '' : ' is-text'}`}>
            <span className="moova-feature-index">0{n}</span>
            <h3>{tr(`f${n}t`)}</h3>
            <p>{tr(`f${n}d`)}</p>
            {shot && <figure className={`moova-feature-shot${shot.bleed === 'left' ? ' is-left' : ''}`} style={{ '--shot-pos': shot.pos } as CSSProperties}><img src={shot.src} alt="" loading="lazy" decoding="async" /></figure>}
          </Reveal>;
        })}
      </div>
    </div>
  </section>;
}

function Companion({ c, tr }: { c: HomeCopy; tr: Tr }) {
  const [tab, setTab] = useState(0);
  const tabs = [c.browse, c.control, c.sync];
  const descriptions = [tr('cm3d'), tr('cm2d'), tr('cm4d')];
  return <section className="moova-companion moova-section" id="companion"><div className="moova-container moova-companion-grid">
    <Reveal className="moova-companion-copy"><span className="moova-eyebrow">{c.phoneLabel}</span><h2>{c.phoneTitle}<br /><span>{c.phoneTitle2}</span></h2><p className="moova-section-lead">{c.phoneLead}</p>
      <div className="moova-companion-tabs" role="tablist" aria-label={c.phoneDemo}>{tabs.map((label, i) => <button key={label} id={`moova-tab-${i}`} role="tab" aria-selected={tab === i} aria-controls="moova-companion-panel" tabIndex={tab === i ? 0 : -1} onKeyDown={event => { if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) { event.preventDefault(); const next = event.key === 'Home' ? 0 : event.key === 'End' ? 2 : (i + (event.key === 'ArrowRight' ? 1 : 2)) % 3; setTab(next); document.getElementById(`moova-tab-${next}`)?.focus(); } }} onClick={() => setTab(i)}>{label}</button>)}</div>
      <div id="moova-companion-panel" role="tabpanel" aria-labelledby={`moova-tab-${tab}`} className="moova-companion-description"><p>{descriptions[tab]}</p></div>
      <a href="#setup" className="moova-text-link">{tr('cm1t')}<ArrowRight size={16} /></a>
      <div className="moova-app-status"><Smartphone size={16} /><span>Android <small>{tr('devBeta')}</small></span><span>iOS <small>{tr('devSoon')}</small></span></div>
    </Reveal>
    <Reveal className="moova-phone-stage">
      <div className="moova-companion-glow" aria-hidden="true" />
      <div className="moova-companion-iphone"><IPhonePreview screen={tab === 1 ? 'player' : tab === 2 ? 'movies' : 'home'} c={c} interactive onOpenPlayer={() => setTab(1)} /></div>
      <div className="moova-phone-notification" role="status"><span><Tv size={22} /></span><div><strong>{tab === 1 ? c.sent : c.connected}</strong><small>{tab === 1 ? 'Sintel' : tr('cmSyncTitle')}</small></div><Check size={17} /></div>
      <span className="moova-phone-demo-label">{c.phoneDemo}</span>
    </Reveal>
  </div></section>;
}


const QR_SEED = 7;
const rand = (i: number, k: number) => { const x = Math.sin(i * 127.1 + k * 311.7 + QR_SEED) * 43758.5453; return x - Math.floor(x); };

type Module = { x: number; y: number; sx: number; sy: number; sr: number; d: number; tx: number; ty: number; dd: number };

/** Every dark QR module with its scattered start (px), its stagger, its distance from the centre (0..1) and its seat in the play triangle (module units). */
function buildModules(): Module[] {
  const n = QR.size;
  const dark: { x: number; y: number }[] = [];
  QR.rows.forEach((row, y) => { for (let x = 0; x < n; x++) if (row[x] === '1') dark.push({ x, y }); });
  const triangle: { x: number; y: number }[] = [];
  for (let y = 2; y <= n - 2; y += .7) {
    const half = 1 - Math.abs(((y - 2) / (n - 4)) * 2 - 1);
    for (let x = 4; x <= 4 + half * (n - 7); x += .7) triangle.push({ x, y });
  }
  const byPosition = (a: { x: number; y: number }, b: { x: number; y: number }) => a.y - b.y || a.x - b.x;
  dark.sort(byPosition); triangle.sort(byPosition);
  return dark.map((m, i) => {
    const seat = triangle[Math.floor(i * triangle.length / dark.length)];
    const angle = rand(i, 1) * Math.PI * 2, radius = 240 + rand(i, 2) * 460;
    const c = (n - 1) / 2;
    return { ...m, sx: Math.cos(angle) * radius, sy: Math.sin(angle) * radius - 90, sr: (rand(i, 3) - .5) * 520, d: rand(i, 4) * .55, tx: seat.x - m.x, ty: seat.y - m.y, dd: Math.hypot(m.x - c, m.y - c) / (c * Math.SQRT2) };
  });
}

/** Getting started: a real, scannable QR assembles from scattered modules, a pulse runs through it, then it folds into a play button. */
function Setup({ c, tr, lp }: { c: HomeCopy; tr: Tr; lp: Tr }) {
  const track = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const modules = useMemo(buildModules, []);
  const { scrollYProgress: p } = useScroll({ target: track, offset: ['start start', 'end end'] });
  const [chapter, setChapter] = useState(0);
  useMotionValueEvent(p, 'change', value => { if (!reduced) setChapter(value < .42 ? 0 : value < .7 ? 1 : 2); });
  const assemble = useTransform(p, [.03, .4], [0, 1]);
  const wave = useTransform(p, [.44, .68], [-.25, 1.3]);
  const collapse = useTransform(p, [.72, .95], [0, 1]);
  const vars = { '--a': assemble, '--w': wave, '--b': collapse } as unknown as CSSProperties;
  const steps = [
    { title: c.setup1, text: tr('step1d'), to: '/activation', cta: tr('fiCta'), caption: tr('heroTrust2') },
    { title: c.setup2, text: tr('step2d'), to: '/playlist', cta: lp('footPlaylist'), caption: tr('cmSyncTitle') },
    { title: c.setup3, text: tr('step3d'), to: '/pricing', cta: tr('heroCta2'), caption: tr('stPlaying') },
  ];
  return <section ref={track} className={`moova-setup${reduced ? ' is-static' : ''}`} id="setup">
    <div className="moova-setup-stage">
      <div className="moova-container moova-setup-grid">
        <div className="moova-setup-copy">
          <span className="moova-eyebrow">{tr('stepKicker')}</span>
          <h2>{c.setupTitle}<br /><span>{c.setupTitle2}</span></h2>
          <ol className="moova-setup-steps">{steps.map((step, i) => <li key={step.title} className={reduced || chapter === i ? 'is-active' : undefined} aria-current={chapter === i ? 'step' : undefined}>
            <span aria-hidden="true">0{i + 1}</span>
            <div><h3>{step.title}</h3><p>{step.text}</p><Link className="moova-text-link" to={step.to} tabIndex={reduced || chapter === i ? 0 : -1}>{step.cta}<ArrowRight size={15} /></Link></div>
          </li>)}</ol>
        </div>
        <div className="moova-setup-side">
          <motion.div className="moova-setup-qr" style={reduced ? undefined : vars} role="img" aria-label={`QR code · ${QR.text.replace('https://', '')}`}>
            <div className="moova-setup-modules" aria-hidden="true">{modules.map((m, i) => <i key={i} style={{ '--x': m.x, '--y': m.y, '--sx': m.sx, '--sy': m.sy, '--sr': m.sr, '--d': m.d, '--tx': m.tx, '--ty': m.ty, '--dd': m.dd.toFixed(3) } as CSSProperties} />)}</div>
          </motion.div>
          <p className="moova-setup-caption">{reduced ? steps[0].caption : steps[chapter].caption}<small>{QR.text.replace('https://', '')}</small></p>
        </div>
      </div>
    </div>
  </section>;
}

/** Closing: the last word of the line is set huge; the film appears inside its letters, then spills over the whole stage. */
function Final({ c, tr }: { c: HomeCopy; tr: Tr }) {
  const track = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress: p } = useScroll({ target: track, offset: ['start end', 'end start'] });
  const parts = c.finalTitle2.trim().split(' ');
  const word = parts.pop() ?? '';
  const rest = parts.join(' ');

  // The stage is pinned while p runs from ~.28 to ~.72 (section 260svh): the whole story fits in that window.
  const introOpacity = useTransform(p, [.2, .32], [0, 1]);
  const introY = useTransform(p, [.2, .32], [18, 0]);
  const wordScale = useTransform(p, [.2, .5], [.9, 1]);
  const strokeOpacity = useTransform(p, [.22, .32, .48], [0, 1, 0]);
  const fillOpacity = useTransform(p, [.3, .46], [0, 1]);
  const panX = useTransform(p, [.25, .75], ['40%', '60%']);
  const spillOpacity = useTransform(p, [.46, .6], [0, 1]);
  const spillScale = useTransform(p, [.46, .75], [1.08, 1]);
  const ctaOpacity = useTransform(p, [.54, .64], [0, 1]);
  const ctaY = useTransform(p, [.54, .64], [28, 0]);
  const staticMode = Boolean(reduced);
  const vars = { '--pan-x': panX, '--chars': word.length } as unknown as CSSProperties;

  return <section ref={track} className={`moova-final${staticMode ? ' is-static' : ''}`}>
    <div className="moova-final-stage" style={staticMode ? ({ '--chars': word.length } as CSSProperties) : vars}>
      <motion.div className="moova-final-spill" aria-hidden="true" style={staticMode ? undefined : { opacity: spillOpacity, scale: spillScale }} />
      <div className="moova-final-aurora" aria-hidden="true" />
      <div className="moova-final-grain" aria-hidden="true" />

      <div className="moova-final-copy">
        <motion.p className="moova-final-intro" style={staticMode ? undefined : { opacity: introOpacity, y: introY }}>{c.finalTitle} <span>{rest}</span></motion.p>
        <h2 className="moova-final-word" aria-label={`${c.finalTitle} ${c.finalTitle2}`}>
          <motion.span className="moova-final-word-stack" aria-hidden="true" style={staticMode ? undefined : { scale: wordScale }}>
            <motion.i className="is-stroke" style={staticMode ? { opacity: 0 } : { opacity: strokeOpacity }}>{word}</motion.i>
            <motion.i className="is-fill" style={staticMode ? undefined : { opacity: fillOpacity }}>{word}</motion.i>
          </motion.span>
        </h2>
        <motion.div className="moova-final-cta" style={staticMode ? undefined : { opacity: ctaOpacity, y: ctaY }}>
          <p>{c.finalLead}</p>
          <a href="#setup" className="moova-button moova-button-white">{tr('heroCta')}<ArrowRight size={16} /></a>
          <span className="moova-fine">{c.trial}</span>
        </motion.div>
      </div>
    </div>
  </section>;
}

export default function HomePage() {
  const { language, tr, display } = useHome();
  const c = getHomeCopy(language);
  const lp = (key: string) => t('lp', key, language);
  return <div className="moova" id="top">
    <Helmet><title>{tr('metaTitle')}</title><meta name="description" content={tr('metaDesc')} /><meta name="theme-color" content="#0B0B14" /><meta property="og:title" content={tr('metaTitle')} /><meta property="og:description" content={tr('metaDesc')} /><meta property="og:url" content="https://moova.tv/" /><meta name="twitter:title" content={tr('metaTitle')} /><meta name="twitter:description" content={tr('metaDesc')} /></Helmet>
    <a className="moova-skip" href="#main">{c.overview}<ArrowDown size={15} /></a>
    <Header />
    <main id="main">
      <Hero c={c} tr={tr} />
      <Features c={c} tr={tr} />
      <Companion c={c} tr={tr} />
      <Setup c={c} tr={tr} lp={lp} />
      <section className="moova-pricing moova-section" id="pricing"><div className="moova-container"><Reveal className="moova-section-heading"><span className="moova-eyebrow">{tr('prKicker')}</span><h2>{c.pricingTitle}<br /><span>{c.pricingTitle2}</span></h2><p>{tr('prSub')}</p></Reveal>
        <div className="moova-price-grid">{['year', 'lifetime'].map((plan, i) => <Reveal key={plan} className={`moova-price-card${i ? ' is-featured' : ''}`}><div className="moova-price-top"><span>{tr(i ? 'prLife' : 'prAnnual')}</span>{i === 1 && <span className="moova-price-badge">{tr('prBest')}</span>}</div><p className="moova-price-description">{tr(i ? 'prLifeDesc' : 'prAnnualDesc')}</p><div className="moova-price-amount">{display(plan)}<small>{tr(i ? 'prLifeUnit' : 'prAnnualUnit')}</small></div><Link className={`moova-button${i ? '' : ' moova-button-outline'}`} to={`/activation?plan=${i ? 'gold' : 'annual'}`}>{tr(i ? 'prLifeCta' : 'prAnnualCta')}<ArrowRight size={16} /></Link><ul>{[1, 2, 3, 4].map(n => <li key={n}><Check size={16} />{tr(`pr${i ? 'L' : 'A'}${n}`)}</li>)}</ul></Reveal>)}</div>
        <div className="moova-price-notes"><ShieldCheck size={16} /><span>{tr('prBen1')}</span><i /><span>{tr('prBen3')}</span></div><p className="moova-fine moova-price-disclaimer">{tr('prDisclaimer')}</p>
      </div></section>
      <section className="moova-faq moova-section"><div className="moova-container moova-faq-grid"><Reveal><span className="moova-eyebrow">FAQ</span><h2>{c.faqTitle}</h2><a className="moova-text-link" href="mailto:support@moova.tv">{lp('footContact')}<ArrowRight size={16} /></a></Reveal><div>{[c.faq1, c.faq2, c.faq3, c.faq4].map((question, i) => <details key={question}><summary>{question}<Plus size={19} /></summary><p>{[tr('pnpLead'), tr('step1d'), tr('cm1d'), c.faq4answer][i]}</p></details>)}</div></div></section>
      <Final c={c} tr={tr} />
    </main>
    <Footer />
  </div>;
}
